/**
 * Feedback Service - API calls for turn feedback
 */

import { getBackendApiUrl } from '../config/backend';
import { logger } from '../utils/logger';
import { chatSessionKeyHeaders } from './sessionKey';

interface FeedbackResponse {
  success: boolean;
  message?: string;
}

interface LocalFeedback {
  feedback: number | null;
  feedbackText: string | null;
  timestamp: string;
}

/**
 * Submit or update feedback for a specific conversation turn.
 *
 * @param sessionId - Session ID of the conversation
 * @param turnNumber - Turn number (0-indexed)
 * @param feedback - Feedback rating: -1=negative, 0=neutral, 1=positive
 * @param feedbackText - Optional text comment
 * @param getToken - Function to get auth token
 * @param traceId - Langfuse trace ID from SSE metadata. TRAC-518 Phase 8a
 *   follow-up: passing this inline lets the backend skip a DynamoDB
 *   lookup on conversation-turns. Without it, feedback is broken in
 *   us-west-2 since that table is us-east-1 only.
 * @returns Response with success status
 */
export const submitTurnFeedback = async (
  sessionId: string,
  turnNumber: number,
  feedback: number,
  feedbackText: string | null,
  getToken: () => Promise<string | undefined>,
  traceId?: string | null
): Promise<FeedbackResponse> => {
  try {
    const token = await getToken();

    const response = await fetch(
      `${getBackendApiUrl()}/api/conversations/${sessionId}/turns/${turnNumber}/feedback`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          // TRAC-1089: grading a turn is owner-only, like every session call.
          ...chatSessionKeyHeaders(sessionId),
        },
        body: JSON.stringify({
          feedback,
          comment: feedbackText || null,
          traceId: traceId || null,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || 'Failed to submit feedback');
    }

    const data = await response.json();

    // Check if Langfuse recorded the feedback - if not, treat as an error
    if (!data.success) {
      throw new Error(data.message || 'Langfuse feedback was not recorded');
    }

    return data;
  } catch (error) {
    logger.error('Error submitting turn feedback:', error);
    throw error;
  }
};

/**
 * Get feedback for a specific turn from localStorage (for optimistic UI).
 *
 * @param sessionId - Session ID
 * @param turnNumber - Turn number
 * @returns Feedback data or null
 */
export const getLocalFeedback = (sessionId: string, turnNumber: number): LocalFeedback | null => {
  try {
    const key = `feedback_${sessionId}_${turnNumber}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    logger.error('Error reading local feedback:', error);
    return null;
  }
};

/**
 * Save feedback to localStorage for optimistic UI.
 *
 * @param sessionId - Session ID
 * @param turnNumber - Turn number
 * @param feedback - Feedback rating
 * @param feedbackText - Optional text feedback
 */
export const saveLocalFeedback = (
  sessionId: string,
  turnNumber: number,
  feedback: number | null,
  feedbackText: string | null
): void => {
  try {
    const key = `feedback_${sessionId}_${turnNumber}`;
    if (feedback === null && !feedbackText) {
      // Remove local feedback when both feedback and text are cleared
      localStorage.removeItem(key);
    } else {
      const data: LocalFeedback = {
        feedback,
        feedbackText,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(key, JSON.stringify(data));
    }
  } catch (error) {
    logger.error('Error saving local feedback:', error);
  }
};

/**
 * Draft feedback storage for modal persistence across cancel/refresh.
 */
interface DraftFeedback {
  pendingFeedback: number | null;
  draftText: string;
  timestamp: string;
}

/**
 * Get draft feedback from localStorage (for modal persistence).
 */
export const getDraftFeedback = (sessionId: string, turnNumber: number): DraftFeedback | null => {
  try {
    const key = `feedback_draft_${sessionId}_${turnNumber}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    logger.error('Error reading draft feedback:', error);
    return null;
  }
};

/**
 * Save draft feedback to localStorage (when user cancels or on change).
 */
export const saveDraftFeedback = (
  sessionId: string,
  turnNumber: number,
  pendingFeedback: number | null,
  draftText: string
): void => {
  try {
    const key = `feedback_draft_${sessionId}_${turnNumber}`;
    if (pendingFeedback === null && !draftText) {
      localStorage.removeItem(key);
    } else {
      const data: DraftFeedback = {
        pendingFeedback,
        draftText,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(key, JSON.stringify(data));
    }
  } catch (error) {
    logger.error('Error saving draft feedback:', error);
  }
};

/**
 * Clear draft feedback from localStorage (after successful submission).
 */
export const clearDraftFeedback = (sessionId: string, turnNumber: number): void => {
  try {
    const key = `feedback_draft_${sessionId}_${turnNumber}`;
    localStorage.removeItem(key);
  } catch (error) {
    logger.error('Error clearing draft feedback:', error);
  }
};

/**
 * Delete feedback for a specific conversation turn.
 *
 * @param sessionId - Session ID of the conversation
 * @param turnNumber - Turn number (0-indexed)
 * @param getToken - Function to get auth token
 * @returns Response with success status
 */
export const deleteTurnFeedback = async (
  sessionId: string,
  turnNumber: number,
  getToken: () => Promise<string | undefined>
): Promise<FeedbackResponse> => {
  try {
    const token = await getToken();

    const response = await fetch(
      `${getBackendApiUrl()}/api/conversations/${sessionId}/turns/${turnNumber}/feedback`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...chatSessionKeyHeaders(sessionId),
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || 'Failed to delete feedback');
    }

    return await response.json();
  } catch (error) {
    logger.error('Error deleting turn feedback:', error);
    throw error;
  }
};

/**
 * Submit feedback to Datadog LLM Observability for trace correlation.
 *
 * This links user feedback (thumbs up/down) to specific LLM traces
 * using tag-based joining with feedback_id. The feedback_id is set as
 * an attribute on the orchestrator span and used to correlate feedback
 * with the specific trace.
 *
 * @param feedbackId - Unique feedback ID for tag-based joining
 * @param feedback - Feedback value: 1 for thumbs up, -1 for thumbs down
 * @param comment - Optional user comment
 * @param getToken - Function to get auth token
 * @returns Response with success status
 */
export const submitDatadogFeedback = async (
  feedbackId: string,
  feedback: number,
  comment: string | null,
  getToken: () => Promise<string | undefined>
): Promise<FeedbackResponse> => {
  try {
    const token = await getToken();

    // Convert numeric feedback to Datadog format
    const feedbackValue = feedback === 1 ? 'thumbs_up' : 'thumbs_down';

    // Build request body with feedback_id for tag-based joining
    const requestBody: Record<string, string | undefined> = {
      feedback_id: feedbackId,
      feedback: feedbackValue,
      comment: comment || undefined,
    };

    const response = await fetch(
      `${getBackendApiUrl()}/api/survey/llm-feedback`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || 'Failed to submit Datadog feedback');
    }

    return await response.json();
  } catch (error) {
    logger.error('Error submitting Datadog feedback:', error);
    throw error;
  }
};
